import React, { useCallback, useContext, useRef, useState } from "react";
import "@amzn/books-design-system-web/build/dist/src/components/BDS/Icon/Icon";
import styles from "./AddToListButton.module.scss";
import WorkflowContext from "src/contexts/WorkflowContext";
import TranslationsContext from "src/contexts/TranslationsContext";
import DeviceContext from "src/contexts/DeviceContext";
import { newMetricsWithContext, recordBehavioralMetric, recordMetricOnce } from "src/utils/metricsUtils";
import focusHelper from "src/utils/focusHelper";
import useImpressionCallback from "src/utils/useImpressionCallback";
import HeartCxContext from "src/contexts/HeartCxContext";
import { addToWishlist, AddToWishlistResponse, removeFromWishlistsGQL, RemoveFromWishlistsResponse } from "src/utils/ajaxUtils";
import debug from "src/utils/debugUtils";
import ToastContext from "src/contexts/ToastContext";

// Inline SVGs so we can set colors using `currentColor` in `fill`/`stroke`
const FilledHeartIcon = () => (
    <bds-icon iconName="heart-filled.svg" iconSize="30px" />
);

const UnfilledHeartIcon = () => (
    <bds-icon iconName="heart.svg" iconSize="30px" />
);

type PropTypes = {
    asin: string;
    isMetadataHearted?: boolean;
    isMiniHeader?: boolean;
};

function onLongPress(onLongPress: () => void, onClick: () => void, ms = 500) {
    const [longPressTriggered, setLongPressTriggered] = useState(false);
    const timeout = useRef<NodeJS.Timeout>();
    const start = useCallback(() => {
        setLongPressTriggered(false);
        timeout.current = setTimeout(() => {
            onLongPress();
            setLongPressTriggered(true);
        }, ms)
    }, [longPressTriggered]);
    const stop = useCallback(() => {
        timeout.current && clearTimeout(timeout.current);
        !longPressTriggered && onClick();
    }, [longPressTriggered]);
    return {
        onTouchStart: start,
        onTouchMove: stop,
        onTouchEnd: stop,
    };
}

const HeartCxButton = ({ asin, isMetadataHearted }: { asin: string, isMetadataHearted: boolean }) => {
    const a11yTarget = useRef<HTMLButtonElement>(null);
    const deviceContext = useContext(DeviceContext);
    const heartCxContext = useContext(HeartCxContext);
    const translations = useContext(TranslationsContext);
    const workflowContext = useContext(WorkflowContext);
    const toastContext = useContext(ToastContext);
    const isHearted = heartCxContext.isHearted(asin, isMetadataHearted);
    const metrics = newMetricsWithContext("HeartCxButton", asin);
    const failureText = translations.getText("add-to-list-failure-text");

    let actionInProgress = false;
    const requestA11yFocus = useCallback(() => {
        focusHelper.requestFocus(a11yTarget.current);
    }, [a11yTarget]);

    useImpressionCallback(a11yTarget, () => metrics.recordMetricOnce("HeartCxButton.Render", 1));

    const heartedSuccess = (response: AddToWishlistResponse) => {
        if (response.response?.wishlists && response.response?.wishlists?.[0]?.name) {
            toastContext.infoMessage(translations.formatText("heart-list-info-message", {"listName": response.response?.wishlists?.[0].name}));
        } else {
            toastContext.infoMessage(translations.getText("heart-info-message"));
        }
    };

    const unheartedSuccess = (response: RemoveFromWishlistsResponse[]) => {
        if (response.length === 1 && response[0].wishlistName) {
            toastContext.infoMessage(translations.formatText("unheart-list-info-message", {"listName": response[0].wishlistName}));
        } else {
            toastContext.infoMessage(translations.getText("unheart-all-info-message"));
        }
    };

    const handleTap = () => {
        if (actionInProgress) {
            return;
        }
        actionInProgress = true;
        if (heartCxContext.isHearted(asin, isMetadataHearted)) {
            metrics.recordBehavioralMetric("HeartCxButton.Unheart.Click", 1);
            heartCxContext.unheartAsin(asin);
            removeFromWishlistsGQL(asin, undefined)
                .then(unheartedSuccess)
                .catch((error) => {
                    debug.error(error);
                    metrics.recordOperationalMetric("RemoveFromList.failure", 1.0);
                    heartCxContext.heartAsin(asin);
                    workflowContext.infoMessage(failureText);
                })
                .finally(() => (actionInProgress = false));
        } else {
            metrics.recordBehavioralMetric("HeartCxButton.Heart.Click", 1);
            heartCxContext.heartAsin(asin);
            addToWishlist(asin, undefined)
                .then(heartedSuccess)
                .catch((error) => {
                    debug.error(error);
                    metrics.recordOperationalMetric("AddToList.failure", 1.0);
                    heartCxContext.unheartAsin(asin);
                    workflowContext.infoMessage(failureText);
                })
                .finally(() => (actionInProgress = false));
        }
    };

    const showAddToList = () => {
        workflowContext.addToList(asin, () => {
            requestA11yFocus();
            actionInProgress = false;
        });
    };

    const handleHold = () => {
        if (actionInProgress) {
            return;
        }
        actionInProgress = true;
        if (heartCxContext.isHearted(asin, isMetadataHearted)) {
            metrics.recordBehavioralMetric("HeartCxButton.Unheart.LongPress", 1);
            heartCxContext.unheartAsin(asin);
            removeFromWishlistsGQL(asin, undefined)
                .then(unheartedSuccess)
                .catch((error) => {
                    debug.error(error);
                    metrics.recordOperationalMetric("RemoveFromList.failure", 1.0);
                    heartCxContext.unheartAsin(asin);
                    workflowContext.infoMessage(failureText);
                })
                .finally(() => (actionInProgress = false));
        } else {
            metrics.recordBehavioralMetric("HeartCxButton.Heart.LongPress", 1);
            showAddToList();
        }
    }

    return (
        <button
            className={deviceContext.device.isIOS ? styles.heartCxButtoniOS : styles.heartCxButton}
            aria-label={isHearted ? translations.getText("hearted-state-a11y-label") : translations.getText("unhearted-state-a11y-label")}
            ref={a11yTarget}
            {...onLongPress(handleHold, handleTap, 300)}
            >
                {isHearted ? <FilledHeartIcon/> : <UnfilledHeartIcon/>}
        </button>
    );
};

const AddToListButton: React.FC<PropTypes> = ({ asin, isMetadataHearted, isMiniHeader }: PropTypes) => {
    const deviceContext = useContext(DeviceContext);
    const workflowContext = useContext(WorkflowContext);
    const translations = useContext(TranslationsContext);
    const a11yTarget = useRef<HTMLButtonElement>(null);
    const showHeartCx = deviceContext.enableHeartCx && !isMiniHeader;
    const buttonName = showHeartCx ? "HeartCxButton" : "AddToListButton";
    const metricsContext = { namespace: isMiniHeader ? "AddToListButton.miniHeader" : buttonName, qv_asin: asin };

    const requestA11yFocus = useCallback(() => {
        focusHelper.requestFocus(a11yTarget.current);
    }, [a11yTarget]);

    useImpressionCallback(a11yTarget, () => recordMetricOnce(metricsContext, `${buttonName}.Render`, 1));

    const addToList = () => {
        recordBehavioralMetric(metricsContext, `${buttonName}.click`, 1);
        workflowContext.addToList(asin, requestA11yFocus);
    }

    if (deviceContext.enableHeartCx && !isMiniHeader) {
        return <HeartCxButton asin={asin} isMetadataHearted={isMetadataHearted === true} />
    }

    return (
        <button type="button" className={[styles.addToListButton, styles[deviceContext.theme]].join(" ")} onClick={addToList} ref={a11yTarget}>
            {translations.getText("add-to-list")}
        </button>
    );
};

export default AddToListButton;
